import {
  DataShopTable,
  ScheduleShopUpdateBeautyInput,
} from "@okeys/shared-utils"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { fetchUserAttributes } from "aws-amplify/auth"
import { customAxios } from "../utils/customAxios/customAxios"
import { getToken } from "../utils/functions/getToken"

// Types pour l'upload d'images
interface ImageUploadInput {
  image: string // Image encodée en base64
  filename: string // Nom du fichier
  contentType: string // Type MIME
  replaceIndex?: number // Index pour remplacer une image existante
}

// Fonction utilitaire pour convertir File en base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = (error) => reject(error)
  })
}

export const useShopDataQuery = () => {
  const { isPending, isError, data, isSuccess, refetch, error } =
    useQuery<DataShopTable>({
      queryKey: ["shopData"],
      queryFn: async () => {
        const token = await getToken()
        const userAttributes = await fetchUserAttributes()
        const shopId = userAttributes["custom:shopId"]

        if (!shopId) {
          throw new Error("Shop ID not found in attributes")
        }

        const response = await customAxios.get(`/data-shop?id=${shopId}`, {
          headers: {
            Authorization: token,
          },
        })
        return response.data
      },
    })
  return {
    pendingShopData: isPending,
    errorShopData: isError,
    successShopData: isSuccess,
    refetchShopData: refetch,
    shopData: data as DataShopTable,
    error: error,
  }
}

export const useAddImageMutation = () => {
  const queryClient = useQueryClient()
  const { mutate, isPending, isError, error } = useMutation({
    mutationFn: async ({
      file,
      replaceIndex,
    }: {
      file: File
      replaceIndex?: number
    }) => {
      // Vérifier la limite d'images avant l'upload
      const currentShopData = queryClient.getQueryData([
        "shopData",
      ]) as DataShopTable
      const currentImages = currentShopData?.urlsImage || []

      // Si on ajoute (pas de remplacement) et qu'on a déjà 7 images
      if (replaceIndex === undefined && currentImages.length >= 7) {
        throw new Error(
          `Limite d'images atteinte (${currentImages.length}/7). Vous devez d'abord supprimer une image existante.`
        )
      }

      const token = await getToken()

      // Convertir le fichier en base64
      const base64 = await fileToBase64(file)

      // Préparer les données pour l'API
      const uploadData: ImageUploadInput = {
        image: base64,
        filename: file.name,
        contentType: file.type,
        replaceIndex: replaceIndex,
      }

      // Appel API
      const response = await customAxios.put("/data-shop/image", uploadData, {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      })

      return response.data
    },
    onSuccess: (data, variables) => {
      // Mettre à jour le cache avec la nouvelle image
      queryClient.setQueryData(["shopData"], (oldData: DataShopTable) => {
        if (!oldData) return oldData

        const currentImages = oldData.urlsImage || []
        const newImages = [...currentImages]

        if (
          variables.replaceIndex !== undefined &&
          variables.replaceIndex >= 0 &&
          variables.replaceIndex < currentImages.length
        ) {
          // Remplacement
          newImages[variables.replaceIndex] = data.imageUrl
        } else {
          // Ajout
          newImages.push(data.imageUrl)
        }

        return {
          ...oldData,
          urlsImage: newImages,
        } as DataShopTable
      })
    },
  })

  return {
    addImageMutation: mutate,
    isUploadingImage: isPending,
    uploadImageError: error,
    isUploadError: isError,
  }
}

export const useDeleteImageMutation = () => {
  const queryClient = useQueryClient()
  const { mutate, isPending, isError, error } = useMutation({
    mutationFn: async ({ imageIndex }: { imageIndex: number }) => {
      const token = await getToken()

      // Appel API pour supprimer l'image
      const response = await customAxios.delete("/data-shop/image", {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        data: {
          imageIndex: imageIndex,
        },
      })

      return response.data
    },
    onSuccess: (_data, variables) => {
      // Mettre à jour le cache en supprimant l'image du tableau
      queryClient.setQueryData(["shopData"], (oldData: DataShopTable) => {
        if (!oldData) return oldData

        const currentImages = oldData.urlsImage || []
        const newImages = currentImages.filter(
          (_, index: number) => index !== variables.imageIndex
        )

        return {
          ...oldData,
          urlsImage: newImages,
        } as DataShopTable
      })
    },
  })

  return {
    deleteImageMutation: mutate,
    isDeletingImage: isPending,
    deleteImageError: error,
    isDeleteError: isError,
  }
}

export const useUpdateLogoMutation = () => {
  const queryClient = useQueryClient()
  const { mutate, isPending, isError, error } = useMutation({
    mutationFn: async ({ file }: { file: File }) => {
      const token = await getToken()

      // Convertir le fichier en base64
      const base64 = await fileToBase64(file)

      // Préparer les données pour l'API
      const uploadData = {
        image: base64,
        filename: file.name,
        contentType: file.type,
      }

      // Appel API
      const response = await customAxios.put("/data-shop/logo", uploadData, {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      })

      return response.data
    },
    onSuccess: (data) => {
      // Mettre à jour le cache avec le nouveau logo
      queryClient.setQueryData(["shopData"], (oldData: DataShopTable) => {
        if (!oldData) return oldData

        return {
          ...oldData,
          urlLogo: data.logoUrl,
        } as DataShopTable
      })
    },
  })

  return {
    updateLogoMutation: mutate,
    isUploadingLogo: isPending,
    uploadLogoError: error,
    isUploadLogoError: isError,
  }
}

export const useDeleteLogoMutation = () => {
  const queryClient = useQueryClient()
  const { mutate, isPending, isError, error } = useMutation({
    mutationFn: async () => {
      const token = await getToken()

      // Appel API pour supprimer le logo
      const response = await customAxios.delete("/data-shop/logo", {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      })

      return response.data
    },
    onSuccess: () => {
      // Mettre à jour le cache en supprimant le logo
      queryClient.setQueryData(["shopData"], (oldData: DataShopTable) => {
        if (!oldData) return oldData

        return {
          ...oldData,
          urlLogo: "", // Logo vide après suppression
        } as DataShopTable
      })
    },
  })

  return {
    deleteLogoMutation: mutate,
    isDeletingLogo: isPending,
    deleteLogoError: error,
    isDeleteLogoError: isError,
  }
}

// Hook utilitaire pour vérifier la limite d'images
export const useImageLimitCheck = () => {
  const queryClient = useQueryClient()

  const checkImageLimit = () => {
    const currentShopData = queryClient.getQueryData([
      "shopData",
    ]) as DataShopTable
    const currentImages = currentShopData?.urlsImage || []
    return {
      isMaxReached: currentImages.length >= 7,
      currentCount: currentImages.length,
      maxCount: 7,
      canAdd: currentImages.length < 7,
    }
  }

  return { checkImageLimit }
}

export const useFidelityNumberMutation = () => {
  const queryClient = useQueryClient()
  const { mutate } = useMutation({
    mutationFn: async ({ fidelityNumber }: { fidelityNumber: number }) => {
      const token = await getToken()
      await customAxios.put(
        "/data-shop/fidelity-number",
        {
          fidelityNumber,
        },
        {
          headers: {
            Authorization: token,
          },
        }
      )
      queryClient.setQueryData(["shopData"], (oldData: DataShopTable) => ({
        ...oldData,
        fidelityNumber: fidelityNumber,
      }))
    },
  })
  return {
    fidelityNumberMutation: mutate,
  }
}

export const useScheduleMutation = () => {
  const queryClient = useQueryClient()
  const { mutate } = useMutation({
    mutationFn: async ({ shopSchedule }: ScheduleShopUpdateBeautyInput) => {
      const token = await getToken()
      await customAxios.put(
        "/dataShop/schedule",
        {
          shopSchedule,
        },
        { headers: { Authorization: token } }
      )
      queryClient.setQueryData(["shopData"], (oldData: DataShopTable) => ({
        ...oldData,
        shopSchedule: shopSchedule,
      }))
    },
  })
  return {
    scheduleMutation: mutate,
  }
}
